import React from "react";
import { FileText, ClipboardCheck, Package, Heart, DollarSign, ChevronRight, AlertCircle, Star, TrendingUp, MapPin, Calendar, Building2, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { OpportunityDialog } from "../pages/ItineraryDetailsPage"; // Import from the actual module

// Enhanced Opportunities Dialog Component
const EnhancedOpportunitiesDialog = ({ enhancedOpportunities, customerName }) => {
  if (!enhancedOpportunities || enhancedOpportunities.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-1">Enhanced Opportunities</h3>
          <p className="text-sm text-muted-foreground">Complete opportunity data for {customerName}</p>
        </div>
        <div className="flex items-center justify-center text-sm text-muted-foreground py-8">
          <AlertCircle className="h-4 w-4 mr-2" />
          No enhanced opportunities available
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">Enhanced Opportunities ({enhancedOpportunities.length})</h3>
        <p className="text-sm text-muted-foreground">Complete opportunity data with flattened and detailed information</p>
      </div>
      <ScrollArea className="max-h-[70vh]">
        <div className="space-y-4 pr-4">
          {enhancedOpportunities.map((opportunity, i) => {
            const { flattened, detailed, value_id, opportunity_type } = opportunity;
            
            return (
              <Card key={i} className="border-l-4 border-l-primary">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {opportunity_type}
                      </Badge>
                      <span className="text-sm font-medium">{value_id}</span>
                    </div>
                    {detailed?.Value && (
                      <Badge variant="secondary" className="text-xs">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                          notation: "compact",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(detailed.Value)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Flattened Data Section */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      Location & Customer Data
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {/* Location Information */}
                      {flattened?.street_address && (
                        <div className="flex items-center gap-1 col-span-full">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span>{flattened.street_address}, {flattened.location_city}, {flattened.location_state} {flattened.location_zip}</span>
                        </div>
                      )}
                      
                      {/* GPS Coordinates */}
                      {(flattened?.location_lat && flattened?.location_lon) && (
                        <div className="text-xs text-muted-foreground">
                          <span>GPS: {flattened.location_lat}, {flattened.location_lon}</span>
                        </div>
                      )}
                      
                      {/* Sales Information */}
                      {flattened?.sales_team && (
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          <span>{flattened.sales_team}</span>
                        </div>
                      )}
                      
                      {flattened?.sales_region && (
                        <div>
                          <span className="text-muted-foreground">Region:</span> {flattened.sales_region}
                        </div>
                      )}
                      
                      {/* Customer Metrics */}
                      {flattened?.orders_total_count && (
                        <div>
                          <span className="text-muted-foreground">Total Orders:</span> {flattened.orders_total_count}
                        </div>
                      )}
                      
                      {flattened?.orders_avg_value && (
                        <div>
                          <span className="text-muted-foreground">Avg Order Value:</span> {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(flattened.orders_avg_value)}
                        </div>
                      )}
                      
                      {/* Company & Type Information */}
                      {flattened?.company_id && (
                        <div>
                          <span className="text-muted-foreground">Company ID:</span> {flattened.company_id}
                        </div>
                      )}
                      
                      {flattened?.type_key && (
                        <div>
                          <span className="text-muted-foreground">Type:</span> {flattened.type_key}
                        </div>
                      )}
                      
                      {/* Revenue Information - Full Width */}
                      {flattened?.revenue_lifetime && (
                        <div className="col-span-full bg-muted/20 p-2 rounded border">
                          <span className="text-muted-foreground font-medium">Lifetime Revenue:</span> 
                          <span className="ml-2 font-bold text-green-600">
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(flattened.revenue_lifetime)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Detailed Data Section */}
                  {detailed && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {opportunity_type} Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        {/* Common Fields */}
                        {detailed.CustomerName && (
                          <div>
                            <span className="text-muted-foreground">Customer:</span> {detailed.CustomerName}
                          </div>
                        )}
                        
                        {detailed.CustomerCode && (
                          <div>
                            <span className="text-muted-foreground">Customer Code:</span> {detailed.CustomerCode}
                          </div>
                        )}
                        
                        {detailed.CompanyID && (
                          <div>
                            <span className="text-muted-foreground">Company ID:</span> {detailed.CompanyID}
                          </div>
                        )}
                        
                        {detailed.EntryDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span>{new Date(detailed.EntryDate).toLocaleDateString()}</span>
                          </div>
                        )}
                        
                        {detailed.Status && (
                          <div>
                            <span className="text-muted-foreground">Status:</span> 
                            <Badge variant={detailed.Status === "OPEN" ? "default" : "secondary"} className="ml-1 text-xs">
                              {detailed.Status}
                            </Badge>
                          </div>
                        )}
                        
                        {detailed.Market && (
                          <div>
                            <span className="text-muted-foreground">Market:</span> {detailed.Market}
                          </div>
                        )}
                        
                        {detailed.SalesRegion && (
                          <div>
                            <span className="text-muted-foreground">Sales Region:</span> {detailed.SalesRegion}
                          </div>
                        )}
                        
                        {detailed.SalesTeam && (
                          <div>
                            <span className="text-muted-foreground">Sales Team:</span> {detailed.SalesTeam}
                          </div>
                        )}
                        
                        {/* Quote-specific fields */}
                        {opportunity_type === 'Quote' && detailed.QuoteNumber && (
                          <div>
                            <span className="text-muted-foreground">Quote Number:</span> {detailed.QuoteNumber}
                          </div>
                        )}
                        
                        {/* Order-specific fields */}
                        {opportunity_type === 'Order' && detailed.OrderNumber && (
                          <div>
                            <span className="text-muted-foreground">Order Number:</span> {detailed.OrderNumber}
                          </div>
                        )}
                        
                        {/* Rental-specific fields */}
                        {opportunity_type === 'Rental' && detailed.ProductCode && (
                          <div>
                            <span className="text-muted-foreground">Product Code:</span> {detailed.ProductCode}
                          </div>
                        )}
                        
                        {opportunity_type === 'Rental' && detailed.CommonName && (
                          <div>
                            <span className="text-muted-foreground">Equipment:</span> {detailed.CommonName}
                          </div>
                        )}
                        
                        {opportunity_type === 'Rental' && detailed.Description && (
                          <div className="col-span-full">
                            <span className="text-muted-foreground">Description:</span> {detailed.Description}
                          </div>
                        )}
                        
                        {/* Project Location */}
                        {(detailed.ProjectCity && detailed.ProjectState) && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span>{detailed.ProjectCity}, {detailed.ProjectState}</span>
                          </div>
                        )}
                        
                        {/* Value - Prominent Display */}
                        {detailed.Value && (
                          <div className="col-span-full bg-primary/10 p-2 rounded border border-primary/20">
                            <span className="text-muted-foreground font-medium">{opportunity_type} Value:</span>
                            <span className="ml-2 font-bold text-primary">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(detailed.Value)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// Helper function to get the opportunity number based on type
const getOpportunityNumber = (opportunity) => {
  const { detailed, opportunity_type } = opportunity;
  if (opportunity_type === 'Quote') return detailed?.QuoteNumber;
  if (opportunity_type === 'Order') return detailed?.OrderNumber;
  if (opportunity_type === 'Rental') return detailed?.ProductCode;
  return null;
};


export default function PrioritizedDataCard({ customer }) {
  // Log the raw input for debugging
  console.log(`[PrioritizedDataCard] Customer ID: ${customer?.id}`, {
    primary_focus: customer?.primary_focus,
    qorc_ranking: customer?.qorc?.ranking,
    open_quotes_length: customer?.open_quotes?.length,
    open_orders_length: customer?.open_orders?.length,
    active_rentals_length: customer?.active_rentals?.length,
    enhanced_opportunities_length: customer?.enhanced_opportunities?.length,
    has_enhanced_opportunities: !!customer?.enhanced_opportunities?.length,
  });

  // Log enhanced opportunities structure if available
  if (customer?.enhanced_opportunities?.length > 0) {
    console.log(`[PrioritizedDataCard] Enhanced opportunities for ${customer.id}:`, {
      total_count: customer.enhanced_opportunities.length,
      opportunity_types: [...new Set(customer.enhanced_opportunities.map(opp => opp.opportunity_type))],
      sample_opportunity: customer.enhanced_opportunities[0],
      flattened_fields: customer.enhanced_opportunities[0]?.flattened ? Object.keys(customer.enhanced_opportunities[0].flattened) : [],
      detailed_fields: customer.enhanced_opportunities[0]?.detailed ? Object.keys(customer.enhanced_opportunities[0].detailed) : [],
    });
  } else {
    console.log(`[PrioritizedDataCard] No enhanced opportunities found for customer ${customer.id}. Available keys:`, Object.keys(customer || {}));
  }

  // --- 1. Determine Primary Focus --- 
  let primaryFocusType = null; // Q, O, R, or C
  if (customer?.primary_focus) {
    const focusLower = customer.primary_focus.toLowerCase();
    if (focusLower.startsWith("quote")) primaryFocusType = "Q";
    else if (focusLower.startsWith("order")) primaryFocusType = "O";
    else if (focusLower.startsWith("rental")) primaryFocusType = "R";
    else if (focusLower.startsWith("retention") || focusLower.startsWith("churn")) primaryFocusType = "C";
  } 
  // Fallback to QORC ranking if primary_focus didn't give a clear type
  if (!primaryFocusType && customer?.qorc?.ranking) {
      primaryFocusType = customer.qorc.ranking.charAt(0).toUpperCase();
      console.log(`[PrioritizedDataCard] Customer ID: ${customer?.id} - Focus derived from ranking: ${primaryFocusType}`);
  }

  console.log(`[PrioritizedDataCard] Customer ID: ${customer?.id} - Determined Focus Type: ${primaryFocusType}`);

  // --- 2. Define all data types --- 
  const dataTypes = {
    Q: {
      data: customer.open_quotes,
      label: "quotes",
      icon: <FileText className="w-4 h-4" />,
      title: "Open Quotes",
      description: "Focus on converting these quotes to orders.",
      borderColor: "border-l-indigo-500",
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-700"
    },
    O: {
      data: customer.open_orders,
      label: "orders",
      icon: <ClipboardCheck className="w-4 h-4" />,
      title: "Open Orders",
      description: "Focus on servicing these existing orders.",
      borderColor: "border-l-green-500",
      bgColor: "bg-green-500/10",
      textColor: "text-green-700"
    },
    R: {
      data: customer.active_rentals,
      label: "rentals",
      icon: <Package className="w-4 h-4" />,
      title: "Active Rentals",
      description: "Focus on managing these active equipment rentals.",
      borderColor: "border-l-blue-500",
      bgColor: "bg-blue-500/10",
      textColor: "text-blue-700"
    },
    C: {
      data: null,
      label: "churn",
      icon: <Heart className="w-4 h-4" />,
      title: "Retention Focus",
      description: customer?.primary_focus || "This customer needs attention to prevent churn.",
      borderColor: "border-l-amber-500",
      bgColor: "bg-amber-500/10",
      textColor: "text-amber-700"
    }
  };

  // --- 3. Helper function to render a data section --- 
  const renderDataSection = (focusType, isPrimary = false) => {
    const config = dataTypes[focusType];
    if (!config) return null;

    const { data, label, icon, title, description, borderColor, bgColor, textColor } = config;
    
    // Check if data is available
    let isDataAvailable = false;
    let sortedData = [];
    
    if (focusType !== "C" && data) {
      isDataAvailable = Array.isArray(data) && data.length > 0;
      
      if (isDataAvailable) {
        sortedData = [...data];
        // Sort quotes and orders by dollar value in descending order
        if (label === "quotes" || label === "orders") {
          sortedData.sort((a, b) => {
            const valueA = a.estimatedvalue || a.amount || 0;
            const valueB = b.estimatedvalue || b.amount || 0;
            return valueB - valueA; // Descending order (highest first)
          });
        }
      }
    }

    // Special case for Churn focus
    if (focusType === "C") {
      return (
        <div className={isPrimary ? "" : "opacity-75"}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-1.5">
              {icon}
              {title}
              {isPrimary && (
                <Badge variant="outline" className="ml-2 text-xs">
                  <Star className="w-3 h-3 mr-1" />
                  PRIMARY
                </Badge>
              )}
            </h3>
          </div>
          <div className={`${bgColor} ${textColor} border ${borderColor.replace('border-l-', 'border-')} p-3 rounded-md`}>
            <p className="text-sm font-medium mb-1">Customer Retention Priority</p>
            <p className="text-sm">{description}</p>
          </div>
          {!isPrimary && (
            <p className="text-xs text-muted-foreground mt-2">
              Review retention metrics and engagement history.
            </p>
          )}
        </div>
      );
    }

    // For Q, O, R types with no data
    if (!isDataAvailable) {
      return (
        <div className={isPrimary ? "" : "opacity-75"}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-1.5">
              {icon}
              {title}
              {isPrimary && (
                <Badge variant="outline" className="ml-2 text-xs">
                  <Star className="w-3 h-3 mr-1" />
                  PRIMARY
                </Badge>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2 p-3 bg-muted/30 border rounded-md text-sm text-muted-foreground">
            <AlertCircle className="w-4 h-4 flex-shrink-0"/>
            <span>No current {label} found.</span>
          </div>
        </div>
      );
    }

    // For Q, O, R types with data
    return (
      <div className={isPrimary ? "" : "opacity-85"}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            {icon}
            {title}
            {isPrimary && (
              <Badge variant="outline" className="ml-2 text-xs">
                <Star className="w-3 h-3 mr-1" />
                PRIMARY
              </Badge>
            )}
          </h3>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
              >
                View All ({sortedData.length})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md w-[90vw]">
              {/* Use OpportunityDialog for quotes/orders, specific render for rentals */}
              {label === "quotes" || label === "orders" ? (
                <OpportunityDialog opportunities={sortedData} dataTypeLabel={label} />
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Active Rentals ({sortedData.length})</h3>
                    <p className="text-sm text-muted-foreground">Current rented equipment for this customer</p>
                  </div>
                  <ScrollArea className="max-h-[60vh]">
                    <div className="space-y-2 pr-4">
                      {sortedData.map((rental, i) => (
                        <div key={i} className="p-3 bg-muted/30 rounded-md border">
                          <div className="flex justify-between items-start gap-2 mb-1">
                            <div className="font-medium">
                              {rental.CommonName || rental.FullDescription || rental.ProductCode || "Unnamed Equipment"}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            {rental.EquipStatus && (
                              <div className="col-span-2 mb-0.5">
                                <span className="inline-block bg-blue-500/10 text-blue-700 px-1.5 py-0.5 rounded-sm">
                                  Status: {rental.EquipStatus === "O" ? "Ongoing" : rental.EquipStatus}
                                </span>
                              </div>
                            )}
                            {rental.StartDate && (
                              <>
                                <span>Start Date:</span>
                                <span>{new Date(rental.StartDate).toLocaleDateString()}</span>
                              </>
                            )}
                            {rental.EndDate && (
                              <>
                                <span>End Date:</span>
                                <span>{new Date(rental.EndDate).toLocaleDateString()}</span>
                              </>
                            )}
                            {rental.ProductCode && (
                              <>
                                <span>Product Code:</span>
                                <span>{rental.ProductCode}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
        
        {isPrimary && (
          <p className="text-sm text-muted-foreground mb-3 mt-2">{description}</p>
        )}
        
        <ScrollArea className={isPrimary ? "h-[150px] sm:h-[180px]" : "h-[100px] sm:h-[120px]"}>
          <div className="space-y-2 pr-3">
            {sortedData.slice(0, isPrimary ? 5 : 3).map((item, i) => {
              if (label === "quotes" || label === "orders") {
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-2 p-2 bg-muted/20 rounded ${borderColor} border ${isPrimary ? '' : 'text-sm'}`}
                  >
                    <div className="min-w-0 flex-grow">
                      <div className={`font-medium ${isPrimary ? 'text-sm' : 'text-xs'} truncate`}>
                        {item.opportunityname || item.ordernumber || "Unnamed"}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {item.markettype && <span>{item.markettype}</span>}
                        {item.lastupdate && (
                          <span>• {new Date(item.lastupdate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    {(item.estimatedvalue || item.amount) && (
                      <div className={`${isPrimary ? 'text-sm' : 'text-xs'} font-medium`}>
                        {new Intl.NumberFormat("en-US", {
                          style: "currency", currency: "USD", notation: "compact",
                          minimumFractionDigits: 0, maximumFractionDigits: 0,
                        }).format(item.estimatedvalue || item.amount || 0)}
                      </div>
                    )}
                  </div>
                );
              } else if (label === "rentals") {
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-2 p-2 bg-muted/20 rounded ${borderColor} border ${isPrimary ? '' : 'text-sm'}`}
                  >
                    <div className="min-w-0 flex-grow">
                      <div className={`font-medium ${isPrimary ? 'text-sm' : 'text-xs'} truncate`}>
                        {item.CommonName || item.FullDescription || item.ProductCode || "Unnamed Equipment"}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        {item.StartDate && (
                          <span>Start: {new Date(item.StartDate).toLocaleDateString()}</span>
                        )}
                        {item.EndDate && (
                          <span>• End: {new Date(item.EndDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // --- 4. Main Render --- 
  
  // If no focus determined at all
  if (!primaryFocusType) {
    return (
      <Card className="bg-background shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <DollarSign className="w-4 h-4" />
            Customer Focus Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground italic">
            Could not determine primary focus for this customer.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Get all other focus types (excluding primary)
  const allFocusTypes = ['Q', 'O', 'R'];
  const secondaryFocusTypes = allFocusTypes.filter(type => type !== primaryFocusType);

  return (
    <Card className="bg-background shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4" />
          Customer Focus Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary Focus Section */}
        <div className="relative">
          <div className="absolute -left-3 top-0 bottom-0 w-1 bg-primary rounded-full" />
          {renderDataSection(primaryFocusType, true)}
        </div>

        {/* Separator */}
        <Separator className="my-4" />

        {/* Secondary Focus Sections */}
        <div className="space-y-4">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Additional Customer Data
          </h4>
          {secondaryFocusTypes.map((focusType) => (
            <div key={focusType} className="pl-2">
              {renderDataSection(focusType, false)}
            </div>
          ))}
        </div>

        {/* Enhanced Opportunities Section */}
        {customer?.enhanced_opportunities && customer.enhanced_opportunities.length > 0 && (
          <>
            <Separator className="my-4" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Enhanced Opportunities
                </h4>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                    >
                      View All ({customer.enhanced_opportunities.length})
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl w-[90vw]">
                    <EnhancedOpportunitiesDialog 
                      enhancedOpportunities={customer.enhanced_opportunities}
                      customerName={customer.name}
                    />
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-3 rounded-lg border border-primary/20">
                <p className="text-sm text-muted-foreground mb-2">
                  Complete opportunity data with enhanced details
                </p>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-background/80 p-2 rounded border">
                    <div className="text-xs text-muted-foreground">Total Opportunities</div>
                    <div className="text-sm font-medium">{customer.enhanced_opportunities.length}</div>
                  </div>
                  <div className="bg-background/80 p-2 rounded border">
                    <div className="text-xs text-muted-foreground">Data Sources</div>
                    <div className="text-sm font-medium">
                      {[...new Set(customer.enhanced_opportunities.map(opp => opp.opportunity_type))].join(", ")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview of first 2 enhanced opportunities */}
              <ScrollArea className="h-[120px]">
                <div className="space-y-2 pr-3">
                  {customer.enhanced_opportunities.slice(0, 2).map((opportunity, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-2 p-2 bg-muted/20 rounded border-l-2 border-l-primary"
                    >
                      <div className="min-w-0 flex-grow">
                        <div className="font-medium text-xs truncate flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            {opportunity.opportunity_type}
                          </Badge>
                          {opportunity.value_id}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {opportunity.flattened?.customer_name && <span>{opportunity.flattened.customer_name}</span>}
                          {opportunity.detailed?.EntryDate && (
                            <span>• {new Date(opportunity.detailed.EntryDate).toLocaleDateString()}</span>
                          )}
                          {opportunity.flattened?.location_city && opportunity.flattened?.location_state && (
                            <span>• {opportunity.flattened.location_city}, {opportunity.flattened.location_state}</span>
                          )}
                        </div>
                      </div>
                      {opportunity.detailed?.Value && (
                        <div className="text-xs font-medium">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency", currency: "USD", notation: "compact",
                            minimumFractionDigits: 0, maximumFractionDigits: 0,
                          }).format(opportunity.detailed.Value)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}